import { useState } from "react";
import { setShippingSourcesForProduct } from "../../analytics/pageLoad";
import PropTypes from "prop-types";
import styles from "./DeliveryAndReturns.scss";
import classnamesBind from "classnames/bind";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  allVariantsPrimarySource,
  getInStockVariants,
} from "../../../utils/variantUtils";
import SoldShippedHeader from "./soldShippedHeader/SoldShippedHeader";
import { getProductSourceSellerInfo } from "../../../utils/sourceSellerHelper";
import { useVariant } from "../../context/variant/VariantContext";
import { useProduct } from "../../context/product/ProductContext";
import { fireAfsAnalytics } from "../../analytics/pageInteraction";
import { usePersonalisedReturnsMessages } from "../../context/personalisedReturnsMessages/PersonalisedReturnsMessagesContext";
import Tooltip from "../tooltip/Tooltip";

const classnames = classnamesBind.bind(styles);

const getIconClass = (iconName) =>
  iconName && iconName !== "none" ? `product-${iconName}` : null;

function fireSourcingAnalytics({
  selectedVariant,
  product,
  sellerId,
  sellerLabel,
  sourceId,
  sourceLabel,
  selectedOrAvailableVariants,
}) {
  if (!selectedVariant) {
    setShippingSourcesForProduct({
      fulfillerId: allVariantsPrimarySource(product.variants)
        ? "primary"
        : sourceId?.toLowerCase(),
      sellerId,
      hasShippedByLabel: !!(sellerLabel || sourceLabel),
      productId: product.id,
      variants: selectedOrAvailableVariants,
    });
  }
}

const getSelectedOrAvailableVariants = ({ selectedVariant, product }) => {
  if (selectedVariant) {
    return [selectedVariant];
  }

  if (product) {
    return getInStockVariants(product.variants);
  }
};

export default function DeliveryAndReturnsReact({
  toggleShippingRestrictions,
}) {
  const {
    getTranslation,
    deliveryApiEndpoint,
    features: { showReturnsMessage },
    deliveryAndReturnsTrackingValue,
    webContext: { browseCountry },
  } = useRuntimeConstants();
  const { product } = useProduct();
  const { selectedVariant } = useVariant();

  const { returnsChargeMessage, isLoading: personalisedReturnsLoading } =
    usePersonalisedReturnsMessages();
  const [openTooltipIndex, setOpenTooltipIndex] = useState(null);
  const selectedOrAvailableVariants = getSelectedOrAvailableVariants({
    selectedVariant,
    product,
  });
  const {
    isAllAsosSold,
    isConsistent,
    sourceId,
    sellerId,
    sellerLabel,
    sourceLabel,
    isPartnerFulfils,
  } = getProductSourceSellerInfo({
    variants: selectedOrAvailableVariants,
  });
  const deliveryAndReturnsLink = `${deliveryApiEndpoint}${
    deliveryAndReturnsTrackingValue
      ? `#feemsg=${encodeURIComponent(deliveryAndReturnsTrackingValue)}`
      : ""
  }`;

  const openShippingRestrictionsModal = (event) => {
    toggleShippingRestrictions(
      true,
      product.shippingRestrictions?.shippingRestrictionsIncludedCountries,
      event.target,
    );
  };

  const getIsraelMessages = () => ({
    delivery: "Delivery fees, if applicable, calculated at checkout.",
    termsLink: getTranslation("delivery_and_returns_policy"),
  });

  const getNonGbMessages = () => ({
    termsLink: getTranslation("delivery_and_returns_policy"),
    delivery: isConsistent
      ? `${getTranslation(
          "delivery_information_threshold_message_qualifying_orders1",
        )}.`
      : getTranslation(
          "delivery_information_threshold_message_no_variant_selected",
        ),
  });

  const getNoSelectedVariantMessages = () => ({
    delivery: isAllAsosSold
      ? "Free delivery on orders over £40.00. Standard delivery £4.50"
      : "Select a size to see delivery options.",
    termsLink: "View Delivery & Returns rates",
  });

  const getSelectedVariantMessages = () => ({
    delivery: !isAllAsosSold
      ? `${getTranslation(
          "delivery_information_threshold_message_qualifying_orders1",
        )}.`
      : "Free delivery on orders over £40.00. Standard delivery £4.50",
    termsLink: !isAllAsosSold
      ? "View our Delivery & Returns Policy"
      : "View Delivery & Returns rates",
  });

  const getMessages = () => {
    if (browseCountry === "IL") {
      return getIsraelMessages();
    }

    if (browseCountry !== "GB") {
      return getNonGbMessages();
    }

    if (!selectedVariant) {
      return getNoSelectedVariantMessages();
    }

    return getSelectedVariantMessages();
  };

  const renderDeliveryMessage = () => {
    const { delivery } = getMessages();
    return (
      <div
        data-testid="deliveryAndReturns__deliveryMessage"
        className={classnames(styles.row, styles.deliveryMessage)}
        aria-live="polite"
      >
        <span className={styles.iconContainer}>
          <span
            className={`${styles.icon} product-delivery-van`}
            aria-hidden="true"
          />
        </span>
        {delivery}
      </div>
    );
  };

  const renderReturnsLabel = () => {
    const messageGroups =
      !personalisedReturnsLoading && returnsChargeMessage
        ? isPartnerFulfils
          ? returnsChargeMessage.partnerFulfilsMessages
          : returnsChargeMessage.messages
        : null;

    const renderLoadingSkeleton = () => (
      <div
        data-testid="deliveryAndReturns__returnsLabel"
        className={styles.row}
        aria-busy="true"
      >
        <span
          data-testid="deliveryAndReturns__returnsLabelSkeleton"
          className={styles.returnsLabelSkeleton}
        />
      </div>
    );

    const renderPersonalisedMessage = (messageGroup, index) => {
      const personalisedText = messageGroup?.content;
      const toolTipContent = messageGroup?.toolTipContent;
      const returnsEntitlementIcon = getIconClass(messageGroup?.icon);
      const textColour = messageGroup?.lightModeColour;

      return (
        <div
          key={index}
          data-testid="deliveryAndReturns__returnsLabel"
          className={classnames(styles.row, styles.returnsLabelRow)}
        >
          {returnsEntitlementIcon && (
            <span className={styles.iconContainer}>
              <span
                className={classnames(styles.icon, returnsEntitlementIcon)}
                aria-hidden="true"
              />
            </span>
          )}
          <span style={textColour ? { color: textColour } : undefined}>
            {personalisedText}
          </span>
          {toolTipContent && (
            <span className={styles.returnsInfoButtonContainer}>
              <button
                type="button"
                data-testid="deliveryAndReturns__returnsInfoButton"
                className={styles.returnsInfoButton}
                aria-label={getTranslation("pdp_misc_more_information")}
                onClick={() => setOpenTooltipIndex(index)}
              >
                <span
                  className={classnames(
                    styles.icon,
                    "product-information-light",
                  )}
                  aria-hidden="true"
                />
              </button>
              <Tooltip
                hide={openTooltipIndex !== index}
                hasBeenClosedCallback={() => setOpenTooltipIndex(null)}
                wrapperClassName={styles.returnsTooltipWrapper}
                tooltipClassName={styles.returnsTooltip}
                showClose={false}
              >
                {toolTipContent}
              </Tooltip>
            </span>
          )}
        </div>
      );
    };

    const renderGenericMessage = () =>
      showReturnsMessage && (
        <div
          data-testid="deliveryAndReturns__returnsLabel"
          className={styles.row}
        >
          <span className={styles.iconContainer}>
            <span
              className={classnames(styles.icon, "product-returns")}
              aria-hidden="true"
            />
          </span>
          {getTranslation(
            isPartnerFulfils
              ? "returns_message_partner"
              : "returns_message_asos",
          )}
        </div>
      );

    return personalisedReturnsLoading
      ? renderLoadingSkeleton()
      : messageGroups?.length
        ? messageGroups.map((messageGroup, index) =>
            renderPersonalisedMessage(messageGroup, index),
          )
        : renderGenericMessage();
  };

  const renderTerms = () => {
    const { termsLink } = getMessages();
    return (
      <div
        data-testid="deliveryAndReturns__terms"
        className={classnames(styles.row, styles.noIcon, styles.terms)}
      >
        <span>
          <a
            data-testid="deliveryAndReturns__termsLink"
            href={deliveryAndReturnsLink}
            target="_blank"
            aria-label={`${termsLink}. ${getTranslation(
              "generic_opens_new_window",
            )}.`}
            rel="noreferrer"
          >
            <span className={styles.termsLinkText} aria-hidden={true}>
              {termsLink}
              <span className={`${styles.icon} product-new-page-small`} />
            </span>
          </a>
        </span>
      </div>
    );
  };

  const renderShippingRestrictions = () => {
    if (product.shippingRestrictions.shippingRestrictionsVisible) {
      return (
        <div
          data-testid="deliveryAndReturns__shippingRestrictions"
          className={classnames(styles.row, styles.shippingRestrictions)}
          aria-live="polite"
        >
          <button
            data-testid="deliveryAndReturns__shippingRestrictionsButton"
            onClick={openShippingRestrictionsModal}
          >
            {product.shippingRestrictions.shippingRestrictionsLabel}
          </button>
        </div>
      );
    }
  };

  if (!product) {
    return null;
  }

  fireSourcingAnalytics({
    selectedVariant,
    product,
    sellerId,
    sellerLabel,
    sourceId,
    sourceLabel,
    selectedOrAvailableVariants,
  });

  return (
    <div
      className={styles.container}
      data-testid="deliveryAndReturns__container"
    >
      <div className={classnames(styles.row, styles.shippingLabel)}>
        <SoldShippedHeader fireSellerInfoAnalytics={fireAfsAnalytics} />
      </div>
      {renderDeliveryMessage()}
      {renderReturnsLabel()}
      {renderTerms()}
      {renderShippingRestrictions()}
    </div>
  );
}

DeliveryAndReturnsReact.propTypes = {
  toggleShippingRestrictions: PropTypes.func.isRequired,
};
